import { Col, Row, Tabs, Typography } from "antd";
import { useEffect, useState, useContext } from "react";
import SiteView from "../../components/configuration/site/SiteView";
import UserView from "../../components/configuration/user/UserView";
import UserGroupView from "../../components/configuration/user_group/UserGroupView";
import GoalView from "../../components/configuration/goal/GoalView";
import RevenueView from "../../components/configuration/revenue/RevenueView";
import TonneProductView from "../../components/configuration/tonne_product/TonneProductView";
import PieceProductView from "../../components/configuration/piece_product/PieceProductView";
import EmissionSourceView from "../../components/configuration/emission_source/EmissionSourceView";
import EmissionFactorView from "../../components/configuration/emission_factor/EmissionFactorView";
import { IndexContext } from "../../context/IndexContext";
import useFetch from "../../hooks/useFetch";
import { useSearchParams } from "react-router-dom";

const { Text, Title } = Typography;

export default function ConfigurationPage() {
  // load index context
  const { authenticationState, notificationApi } = useContext(IndexContext);

  const [tabPosition, setTabPosition] = useState(
    window.innerWidth <= 768 ? "top" : "left",
  );
  const [tabType, setTabType] = useState(
    window.innerWidth <= 768 ? "card" : "line",
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeKey, setActiveKey] = useState(
    searchParams.get("Key")
      ? searchParams.get("Key")
      : authenticationState.user_type < 3
        ? "User"
        : "Goal",
  );

  const [fetchWrapper] = useFetch();

  const [organisationNameFilterList, setOrganisationNameFilterList] = useState(
    [],
  );

  const [organisationNameSelectionList, setOrganisationNameSelectionList] =
    useState([]);

  const tabItems = [
    // ... is conditional spread operator that includes the User Group tab only if authenticationState.user_type === 1.
    ...(authenticationState.user_type === 1
      ? [
          {
            key: "Group",
            children: (
              <UserGroupView
                refreshOrganisationNameFilter={getOrganisationNameFilter}
                refreshOrganisationNameSelection={getOrganisationNameSelection}
              />
            ),
            label: <div className="font-lg">User Group</div>,
          },
        ]
      : []),
    ...(authenticationState.user_type < 3
      ? [
          {
            key: "User",
            children: (
              <UserView
                organisationNameSelectionList={organisationNameSelectionList}
              />
            ),
            label: <div className="font-lg">User</div>,
          },
        ]
      : []),
    ...(authenticationState.user_type < 3
      ? [
          {
            key: "Site",
            children: (
              <SiteView
                organisationNameFilterList={organisationNameFilterList}
                organisationNameSelectionList={organisationNameSelectionList}
              />
            ),
            label: <div className="font-lg">Site</div>,
          },
        ]
      : []),
    {
      key: "Goal",
      children: <GoalView />,
      label: <div className="font-lg">Carbon Accounting Goal</div>,
    },
    {
      key: "Revenue",
      children: <RevenueView />,
      label: <div className="font-lg">Revenue</div>,
    },
    {
      key: "TonneProduct",
      children: <TonneProductView />,
      label: <div className="font-lg">Product (Tonne)</div>,
    },
    {
      key: "PieceProduct",
      children: <PieceProductView />,
      label: <div className="font-lg">Product (Piece)</div>,
    },
    {
      key: "EmissionSource",
      children: <EmissionSourceView />,
      label: <div className="font-lg">Emission Source</div>,
    },
    {
      key: "EmissionFactor",
      children: <EmissionFactorView />,
      label: <div className="font-lg">Emission Factor</div>,
    },
  ];

  /* API to get organisation name filter for site selection (for superuser only) */
  function getOrganisationNameFilter() {
    fetchWrapper({
      endpoint_url: "site_management/getOrganisationNameFilter",
      method: "GET",
      set_content_type_header: false,
      onSuccess: (response) => {
        setOrganisationNameFilterList(response.data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
      },
    });
  }

  /* API to get organisation name list if superuser is adding user */
  function getOrganisationNameSelection() {
    fetchWrapper({
      endpoint_url: "user_management/getOrganisationNameSelection",
      method: "GET",
      set_content_type_header: false,
      onSuccess: (response) => {
        setOrganisationNameSelectionList(response.data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
      },
    });
  }

  useEffect(() => {
    if (authenticationState) {
      if (authenticationState.user_type === 1) {
        getOrganisationNameFilter();
        getOrganisationNameSelection();
      }
    }
  }, [authenticationState]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setTabPosition("top");
        setTabType("card");
      } else {
        setTabPosition("left");
        setTabType("line");
      }
    };

    window.addEventListener("resize", handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <Title level={3} className="mt-2 mb-5 ml-5">
            Configuration
          </Title>
        </Col>
      </Row>
      <Row gutter={[12, 12]} style={{ height: "calc(100% - 60px)" }}>
        <Col span={24} style={{ height: "100%" }}>
          <Tabs
            destroyInactiveTabPane
            items={tabItems}
            size="small"
            tabPosition={tabPosition}
            type={tabType}
            tabBarStyle={{ minWidth: "250px" }}
            style={{ height: "100%" }}
            activeKey={activeKey}
            onChange={(key) => {
              setSearchParams({ Key: key }, true);
              setActiveKey(key);
            }}
          ></Tabs>
        </Col>
      </Row>
    </>
  );
}
